import { isInAppBrowser } from "@/lib/inAppBrowser";

const HOSTED_CHECKOUT = "https://api.razorpay.com/v1/checkout/embedded";

export const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load payment gateway"));
    document.body.appendChild(script);
  });

// Full-page form POST to Razorpay hosted page: no iframe/SDK, works inside WhatsApp/Instagram webviews.
// Preselects UPI so mobile users land directly on the UPI-apps screen (UPI Intent auto-detects & launches
// installed apps like GPay/PhonePe/Paytm), bypassing manual VPA entry. Other methods remain accessible.
export const submitHostedCheckout = ({ order, prefill, description, callbackPath, cancelPath }) => {
  const origin = window.location.origin;
  const fields = {
    key_id: order.key_id,
    order_id: order.order_id,
    amount: order.amount,
    currency: order.currency,
    name: "Binfinite Mind",
    description,
    image: `${origin}/logo.jpeg`,
    "prefill[name]": prefill.name,
    "prefill[email]": prefill.email,
    "prefill[contact]": prefill.contact,
    "prefill[method]": "upi",
    "notes[webview_intent]": "true",
    callback_url: `${origin}${callbackPath}`,
    cancel_url: `${origin}${cancelPath}`,
  };
  const form = document.createElement("form");
  form.method = "POST";
  form.action = HOSTED_CHECKOUT;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value ?? "";
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

// UPI-Intent display config: shows one-tap UPI app icons (GPay/PhonePe/Paytm/BHIM) FIRST, while keeping
// standard UPI available as a fallback so UPI always appears (QR on desktop, UPI ID if an app can't launch).
// cards/netbanking stay available via default blocks.
const UPI_INTENT_CONFIG = {
  display: {
    blocks: {
      upiapps: {
        name: "Pay using a UPI App",
        instruments: [{ method: "upi", flows: ["intent"] }],
      },
    },
    sequence: ["block.upiapps"],
    preferences: { show_default_blocks: true },
  },
};

// Opens payment:
// - In-app browsers (WhatsApp/Instagram webviews): full-page redirect to hosted checkout, since the SDK
//   cannot pop up or app-switch inside those webviews.
// - Everyone else (desktop + mobile browsers): Standard Checkout SDK with UPI Intent so users tap a UPI
//   app and it opens directly (no manual VPA entry). Falls back to hosted checkout if the SDK can't load.
export const startPayment = async ({ order, prefill, description, callbackPath, cancelPath, onSuccess, onFailure, onDismiss }) => {
  let sdkReady = false;
  if (!isInAppBrowser()) {
    try {
      await loadRazorpayScript();
      sdkReady = !!window.Razorpay;
    } catch {
      sdkReady = false;
    }
  }
  if (!sdkReady) {
    submitHostedCheckout({ order, prefill, description, callbackPath, cancelPath });
    return;
  }
  const rzp = new window.Razorpay({
    key: order.key_id,
    amount: order.amount,
    currency: order.currency,
    name: "Binfinite Mind",
    description,
    order_id: order.order_id,
    prefill,
    theme: { color: "#2D5A47" },
    config: UPI_INTENT_CONFIG,
    handler: onSuccess,
    modal: { ondismiss: onDismiss },
  });
  rzp.on("payment.failed", onFailure);
  rzp.open();
};
